// Campaign Responses (#6182) constants and column refs shared by the field engine and custom-reports code.

import moment from 'moment';
import { findBallotWithItems, walkBallotItems } from '../../proxy-voting/ballot-selection.js';

export const CAMPAIGN_RESPONSE_LEVEL = 'campaignResponse';

export const CAMPAIGN_STATUS = {
	SIGNED: 'signed',
	NOT_SIGNED: 'not_signed',
};

export const CAMPAIGN_TYPE = { VOTING: 'voting', SIGNATURES: 'signatures' };
export const CAMPAIGN_TYPES = Object.values(CAMPAIGN_TYPE);
export const CAMPAIGN_TYPE_LABELS = { voting: 'Proxy Voting', signatures: 'Signatures' };

// Local calendar day, matching the admin voting/signatures pages (a UTC render shifts evening signatures a day).
export function formatCampaignSignDate(unixSeconds) {
	const ts = Number(unixSeconds);
	return ts > 0 ? moment.unix(ts).format('YYYY-MM-DD') : '';
}

export function candidateNames(ballot) {
	return (ballot?.candidates || []).filter((c) => c?.trim());
}

// Candidates across every ballot (flat, as voting.js lays them out), then yes/no and checkbox items.
const votingColumnsCache = new WeakMap();

export function getVotingColumns(campaign) {
	const cacheable = campaign !== null && typeof campaign === 'object';
	if (cacheable && votingColumnsCache.has(campaign)) return votingColumnsCache.get(campaign);
	const columns = [];
	(campaign?.ballots || []).forEach((ballot) => {
		candidateNames(ballot).forEach((label) => {
			columns.push({ index: columns.length, kind: 'candidate', label, ballotClass: ballot.class });
		});
	});
	const itemBallot = findBallotWithItems(campaign?.ballots);
	if (itemBallot) {
		walkBallotItems(itemBallot.ballotItems)
			.filter((entry) => entry.type !== 'slate')
			.forEach((entry) => {
				columns.push({ index: columns.length, kind: 'item', label: entry.item?.label || '', ballotClass: itemBallot.class });
			});
	}
	if (cacheable) votingColumnsCache.set(campaign, columns);
	return columns;
}

// ---------- Campaign refs ----------
// Format: type|name|field[|arg[|label]], each segment URI-encoded so names with ':' '|' spaces or parens are safe.
// arg is the votes index for field 'vote' (plus the column label) and the form name for field 'form'.

const REF_SEP = '|';

const encodeSegments = (segments) => segments.map((s) => encodeURIComponent(s)).join(REF_SEP);

export function campaignKey(type, name) {
	return encodeSegments([type, name]);
}

export function encodeCampaignRef({ type, name, field, index, label, form }) {
	if (!CAMPAIGN_TYPES.includes(type)) throw new Error(`Unknown campaign type: ${type}`);
	if (!name || !field) throw new Error('Campaign ref needs a name and field');
	const segments = [type, name, field];
	if (field === 'vote') {
		segments.push(String(index));
		if (label !== undefined && label !== null) segments.push(String(label));
	} else if (field === 'form') {
		segments.push(form);
	}
	return encodeSegments(segments);
}

// Vote refs saved without a label decode with label undefined (no label check).
export function decodeCampaignRef(ref) {
	if (typeof ref !== 'string' || !ref) return null;
	let parts;
	try {
		parts = ref.split(REF_SEP).map(decodeURIComponent);
	} catch {
		return null;
	}
	const [type, name, field, arg, label] = parts;
	if (!CAMPAIGN_TYPES.includes(type) || !name || !field) return null;

	const decoded = { type, name, field, key: campaignKey(type, name) };
	if (field === 'vote') {
		if (!/^\d+$/.test(arg || '')) return null;
		decoded.index = Number(arg);
		if (label !== undefined) decoded.label = label;
	} else if (field === 'form') {
		if (!arg) return null;
		decoded.form = arg;
	}
	return decoded;
}
